import React, { useState, useEffect } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"
import { Icon } from "~/react/ui/icon"
import { Button } from "~/react/ui/button"

const t = (key: string) => window.i18n.t(key)

const filterVariants = cva("collapsable-filter", {
  variants: {
    locked: {
      true: "disabled",
    },
  },
  defaultVariants: {
    locked: false,
  },
})

export interface CollapsableFilterProps
  extends VariantProps<typeof filterVariants> {
  label: string
  icon: string
  tooltip?: string
  count?: number
  hasValues?: boolean
  onClear?: () => void
  isLocked?: boolean
  lockedTooltip?: string
  defaultExpanded?: boolean
  className?: string
  children: React.ReactNode
}

export function CollapsableFilter({
  label,
  icon,
  tooltip,
  count,
  hasValues = false,
  onClear,
  isLocked = false,
  lockedTooltip,
  defaultExpanded,
  className,
  children,
}: CollapsableFilterProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? hasValues)

  // Auto-expand when values are set, auto-collapse when cleared
  // But respect defaultExpanded if explicitly set to true
  useEffect(() => {
    if (hasValues && !isExpanded) {
      setIsExpanded(true)
    } else if (!hasValues && isExpanded && defaultExpanded !== true) {
      // Only auto-collapse if defaultExpanded wasn't explicitly set to true
      setIsExpanded(false)
    }
  }, [hasValues, defaultExpanded])

  // Locked state render
  if (isLocked) {
    return (
      <div className={cn(filterVariants({ locked: true }), className)}>
        <div className="collapsable-filter__header">
          <Icon
            icon={icon}
            className="collapsable-filter__icon"
            aria-hidden="true"
          />
          <div className="collapsable-filter__title">{label}</div>
          <button
            type="button"
            className="collapsable-filter__toggle btn-empty"
            aria-label={t("js.back.discover.filters.toggle")}
            data-bs-toggle="popover"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-container="body"
            data-bs-content={lockedTooltip}
          >
            <Icon icon="lock" aria-hidden="true" size="sm" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(filterVariants({ locked: false }), className)}>
      <div
        className="collapsable-filter__header"
        {...(count && count > 0 ? { "data-count": count } : {})}
      >
        <Icon
          icon={icon}
          className="collapsable-filter__icon"
          aria-hidden="true"
        />
        <div className="collapsable-filter__title">
          {label}
          {tooltip && (
            <button
              type="button"
              className="collapsable-filter__hint question-circle"
              data-controller="tooltip"
              title={tooltip}
            >
              <span className="far fa-info-circle" aria-hidden="true" />
            </button>
          )}
        </div>
        {hasValues && onClear && (
          <Button
            type="button"
            variant="linkGrey"
            size="sm"
            className="collapsable-filter__clear"
            onClick={onClear}
          >
            <strong>{t("js.back.discover.filters.clear")}</strong>
          </Button>
        )}
        <button
          type="button"
          className="collapsable-filter__toggle btn-empty"
          aria-label={t("js.back.discover.filters.toggle")}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon
            icon={`angle-${isExpanded ? "up" : "down"}`}
            aria-hidden="true"
            size="sm"
          />
        </button>
      </div>

      {isExpanded && (
        <div className="collapsable-filter__content">{children}</div>
      )}
    </div>
  )
}
